import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
} from "@tanstack/react-router";
import { Shell } from "./layout/Shell";
import { AuditLogsPage } from "./pages/AuditLogsPage";
import { CustomFieldsPage } from "./pages/CustomFieldsPage";
import { HelpPage } from "./pages/HelpPage";
import { OverviewPage } from "./pages/Overview";
import { RolesPage } from "./pages/RolesPage";
import { SignInMethodPage } from "./pages/SignInMethodPage";
import { ClientDetailPage } from "./pages/clients/ClientDetailPage";
import { ClientsPage } from "./pages/clients/ClientsPage";
import { BrandingPage } from "./pages/settings/BrandingPage";
import { DataPage } from "./pages/settings/DataPage";
import { LogRetentionPage } from "./pages/settings/LogRetentionPage";
import { UserTypesPage } from "./pages/settings/UserTypesPage";
import { GroupDetailPage } from "./pages/groups/GroupDetailPage";
import { GroupsPage } from "./pages/groups/GroupsPage";
import { UserDetailPage } from "./pages/users/UserDetailPage";
import { UsersPage } from "./pages/users/UsersPage";
import { NotFoundPage } from "./pages/NotFound";

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });
const shell = createRoute({ getParentRoute: () => rootRoute, id: "shell", component: Shell });

const page = <P extends string>(path: P, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => shell, path, component });

const routeTree = rootRoute.addChildren([
  shell.addChildren([
    page("/", OverviewPage),
    page("/users", UsersPage),
    page("/users/$id", UserDetailPage),
    page("/sign-in-method", SignInMethodPage),
    page("/groups", GroupsPage),
    page("/groups/$groupId", GroupDetailPage),
    page("/roles", RolesPage),
    page("/custom-fields", CustomFieldsPage),
    page("/oauth-clients", ClientsPage),
    page("/oauth-clients/$clientId", ClientDetailPage),
    page("/audit-logs", AuditLogsPage),
    page("/settings/branding", BrandingPage),
    page("/settings/data", DataPage),
    page("/settings/log-retention", LogRetentionPage),
    page("/settings/user-types", UserTypesPage),
    page("/help", HelpPage),
    page("/help/$topic", HelpPage),
    // Rendered only momentarily: the OIDC callback is processed before the router mounts.
    page("/auth/callback", () => null),
  ]),
]);

export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    defaultPreload: "intent",
    scrollRestoration: true,
    ...(history ? { history } : {}),
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
